import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { ReconciliationSupplierDetail, SaveSoaPayload } from './types';

const getEndpoint = (supplierId: number) => `ums/v1/cms/reconciliation/suppliers/${supplierId}/soa`;

type TData = ReconciliationSupplierDetail;
type TError = AxiosError;
type TVariables = SaveSoaPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useSaveSoaApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ month, supplierId, entries, files, fileRestaurants }: TVariables) => {
    try {
      const body = new FormData();
      body.append('entries', JSON.stringify(entries));
      (files ?? []).forEach((file) => body.append('files', file));
      if (fileRestaurants) body.append('fileRestaurants', JSON.stringify(fileRestaurants));

      const { data } = await http.put(getEndpoint(supplierId), body, {
        params: { month },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status) throw error;

      toast({
        description: error.response?.data?.message ?? 'Failed to save SOA entries',
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    ...options,
    mutationKey: ['reconciliation-save-soa'],
  });
}
